/* shoplift-rapi-display.js - Mountaineer Brand (v1.1.0, 2026-08-26)
 *
 * Keeps the Rapi Bundles widget's DISPLAYED prices consistent with the
 * visitor's Shoplift price-test experience. Display-only: cart, checkout,
 * selling-plan routing, and every theme-tagged price surface are handled by
 * Shoplift's own runtime (verified 2026-08-13 static analysis; see the setup
 * plan, Parts 4b and 7).
 *
 * Source of truth: the theme's [data-sl-attribute-p] / [data-sl-attribute-cap]
 * elements for this product (the sticky ATC bar carries them on every
 * template). Shoplift has already resolved assignment, audience exclusion,
 * preview mode, and control pinning for those elements, so this layer
 * inherits all of it: it simply makes the Rapi widget agree with them.
 * When no test is active the scale factor is exactly 1 and nothing is
 * written (text-equality guard), so the layer is dormant in production
 * until a test runs.
 *
 * Verified live 2026-08-21 against:
 *   /products/natural-deodorant                          (per-unit display, no compare-at, sub toggle)
 *   /products/natural-deodorant-3-pack-best-selling-scents (totals display, compare-at, no Rapi sub toggle)
 *   /products/natural-deodorant-try-em-all-kit           (totals display, compare-at, sub toggle)
 * Observed behaviors this code depends on: Rapi rewrites text in place on
 * subscribe-toggle (same DOM nodes) but ADDS/REMOVES the Single bar's
 * full-price node per state, so baselines store numbers only and elements
 * are re-queried on every pass. Full-price values are cap-derived
 * (compare_at x qty) on compare-at products in BOTH toggle states, and
 * base-derived otherwise. Savings subtitles satisfy
 * save = q x (full - price) with q = pack quantity (per-unit display) or 1
 * (totals display); q is learned from the baseline, never hardcoded.
 * Rounding: values are scaled and rounded to the cent; this can differ from
 * Rapi's native chain by 1 cent - accepted (savings are recomputed from the
 * written values, so the widget is always internally consistent).
 *
 * Kill switch: window.__slRapiDisplayOff = true (or remove the include).
 * Debug:       localStorage.slRapiDebug = "1"  (then reload)
 * QA status:   window.__slRapiDisplay          ({version, active, why})
 *
 * Maintenance: a Rapi widget redesign fails this layer silently back to
 * Rapi's own prices (today's behavior). Re-map the selectors below if the
 * rapi-prestige-bundles class names change.
 *
 * v1.1.0 (2026-08-26, found in draft-test preview QA): Rapi diff-skips DOM
 * writes for elements whose value does not change across the subscribe
 * toggle (e.g. the struck full price on compare-at products), so our own
 * corrected text legitimately survives the state change. v1.0.0's capture
 * guard read that as "Rapi hasn't rendered this state yet" and deferred
 * forever, leaving sub-state bars uncorrected. Baselines are now
 * self-healing: each element's Rapi-authored value is adopted whenever it
 * appears (also recovering from variant/deal changes mid-session), and an
 * element still showing our own write inherits the baseline that write was
 * derived from. The post-pass observer flush now re-schedules when it finds
 * an interleaved non-ours mutation instead of discarding it.
 * Also v1.1.0: the runtime strips the _slpt input for control-side visitors,
 * which silenced the layer for exactly the visitors it must pin on increase
 * tests - pageProductId() now falls back to ShopifyAnalytics.meta.product.id.
 */
(function () {
  'use strict';
  if (window.__slRapiDisplayOff) return;

  var VERSION = '1.1.0';
  var status = { version: VERSION, active: false, why: 'starting' };
  window.__slRapiDisplay = status;

  var DEBUG = false;
  try { DEBUG = localStorage.getItem('slRapiDebug') === '1'; } catch (e) {}
  function log() { if (DEBUG) try { console.log.apply(console, ['[sl-rapi]'].concat([].slice.call(arguments))); } catch (e) {} }

  /* ---------- money text helpers (format observed live: "$ 12.59", suffixes like "/stick" preserved) ---------- */

  function toCents(text) {
    var m = String(text).replace(/[,\s]/g, '').match(/(\d+\.?\d{0,2})/);
    return m ? Math.round(parseFloat(m[1]) * 100) : null;
  }

  /* Every write is recorded (with the baseline cents it was derived from) so
     baseline handling can tell our output apart from Rapi-authored values. */
  var ourWrites = typeof WeakMap === 'function' ? new WeakMap() : null;
  if (!ourWrites) { status.why = 'no WeakMap; dormant'; return; }

  function writeCents(el, cents, srcBaseline) {
    if (!el || cents == null || !isFinite(cents)) return;
    var next = String(el.textContent).replace(/\d[\d,]*\.?\d{0,2}/, (cents / 100).toFixed(2));
    if (el.textContent !== next) {
      el.textContent = next;
      ourWrites.set(el, { text: next, src: srcBaseline == null ? null : srcBaseline });
    }
  }
  function isOurText(el) {
    if (!el) return false;
    var rec = ourWrites.get(el);
    return !!rec && rec.text === el.textContent;
  }
  function setHidden(el, hide) {
    if (!el) return;
    try {
      if (hide) el.style.setProperty('display', 'none', 'important');
      else el.style.removeProperty('display');
    } catch (e) {}
  }

  /* ---------- page context ---------- */

  /* The page's product id. Primary: the _slpt input the Part 1 theme edit
     added. Fallback: Shopify's product meta - REQUIRED because Shoplift's
     runtime REMOVES the _slpt input for control-side visitors (observed in
     control preview 2026-08-26). Without the fallback the layer would go
     dormant for exactly the visitors it must pin on increase tests. */
  function pageProductId() {
    var i = document.querySelector('form[action*="/cart/add"] input[name="properties[_slpt]"]');
    if (i) return i.getAttribute('data-sl-pid');
    try {
      var id = window.ShopifyAnalytics.meta.product.id;
      return id ? String(id) : null;
    } catch (e) { return null; }
  }

  /* Answer key: what base price / compare-at should THIS visitor see?
     Read from the theme elements Shoplift has already corrected. */
  function answerKey(pid) {
    var pEl = document.querySelector('[data-sl-attribute-p="' + pid + '"]');
    if (!pEl) return null;
    var p = toCents(pEl.textContent);
    if (!p) return null;
    var capEl = document.querySelector('[data-sl-attribute-cap="' + pid + '"]');
    var cap = capEl ? toCents(capEl.textContent) : null; /* removed or empty = no compare-at */
    return { p: p, cap: cap && cap > p ? cap : null, pEl: pEl };
  }

  /* Catalog price Rapi computed its display from: Shopify product meta
     (server-rendered cents; mid-test this is the shifted catalog price,
     which is exactly what Rapi saw). Re-read every pass - variant switches
     change it. */
  function catalogBase() {
    try {
      var meta = window.ShopifyAnalytics.meta.product;
      var input = document.querySelector('form[action*="/cart/add"] input[name="id"]');
      var vid = input && input.value;
      var v = null, i;
      for (i = 0; i < meta.variants.length; i++) {
        if (!vid || String(meta.variants[i].id) === String(vid)) { v = meta.variants[i]; break; }
      }
      return v && v.price ? v.price : null;
    } catch (e) { return null; }
  }

  /* ---------- Rapi widget reading ---------- */

  function widgetState(container) {
    var cb = container.querySelector('input[name="custom-box"]');
    return cb && cb.checked ? 'sub' : 'onetime';
  }

  function barEls(bar) {
    var priceEl = bar.querySelector('.rapi-bundles__bar-price .rapi-money') ||
                  bar.querySelector('.rapi-bundles__bar-price');
    var fullEl = bar.querySelector('.rapi-bundles__bar-full-price .rapi-money') ||
                 bar.querySelector('.rapi-bundles__bar-full-price');
    var saveEl = null, subs = bar.querySelectorAll('.rapi-bundles__bar-subtitle'), i;
    for (i = 0; i < subs.length; i++) {
      if (/\$/.test(subs[i].textContent)) { saveEl = subs[i]; break; } /* "Save $ X" only; percent labels are preserved by design */
    }
    return { price: priceEl, full: fullEl, save: saveEl };
  }

  function nearMultiple(value, unit, tol) {
    if (!value || !unit) return false;
    var r = value % unit;
    return Math.min(r, unit - r) <= tol;
  }

  /* cap0: the catalog compare-at Rapi derived struck prices from. Probed
     lazily from the widget in one-time state: the first struck value that is
     not a near-multiple of the catalog base is the compare-at (verified on
     all three target products). Cached per container against the base it was
     probed with. */
  var capProbe = new WeakMap(); /* container -> { base: cents, cap: cents|null } */
  function containerCap(container, base) {
    var rec = capProbe.get(container);
    if (rec && rec.base === base) return rec.cap;
    if (widgetState(container) !== 'onetime') return rec ? rec.cap : null; /* only probe in one-time state */
    var cap = null;
    var full = container.querySelector('.rapi-bundles__bar-full-price .rapi-money') ||
               container.querySelector('.rapi-bundles__bar-full-price');
    if (full && !isOurText(full)) {
      var f = toCents(full.textContent);
      if (f && f > base && !nearMultiple(f, base, 2)) cap = f;
    }
    capProbe.set(container, { base: base, cap: cap });
    return cap;
  }

  /* Baselines: Rapi-authored numbers per bar, per toggle state. Numbers only -
     the sub-state full-price node is added/removed by Rapi, so element refs
     go stale and are re-queried on every pass.

     Self-healing (v1.1.0): Rapi diff-skips DOM writes for values that do not
     change across the subscribe toggle, so an element may legitimately still
     carry OUR text after a state change. Per element, per state:
     - text is Rapi-authored -> adopt it as the baseline (updates stale
       baselines after variant/deal changes too)
     - text is our own write and no baseline is stored yet -> inherit the
       baseline that write was derived from (the diff-skip case)
     - text is our own write and a baseline is stored -> keep it */
  var baselines = new WeakMap(); /* bar -> { onetime: snap, sub: snap } */

  function adoptField(snap, field, el) {
    if (!el) { snap[field] = null; return; }
    var cur = toCents(el.textContent);
    if (cur == null) { snap[field] = null; return; }
    var rec = ourWrites.get(el);
    if (rec && rec.text === el.textContent) {
      if (snap[field] == null && rec.src != null) {
        snap[field] = rec.src;
        log('baseline inherited (rapi diff-skip)', field, rec.src);
      }
    } else if (snap[field] !== cur) {
      snap[field] = cur;
    }
  }

  function baseline(bar, key, els) {
    var rec = baselines.get(bar);
    if (!rec) { rec = {}; baselines.set(bar, rec); }
    var snap = rec[key];
    if (!snap) { snap = { price: null, full: null, save: null }; rec[key] = snap; }
    adoptField(snap, 'price', els.price);
    adoptField(snap, 'full', els.full);
    adoptField(snap, 'save', els.save);
    if (snap.full != null && snap.price != null && snap.full < snap.price) snap.full = null;
    return snap.price ? snap : null;
  }

  /* ---------- the correction pass ---------- */

  function correct(container) {
    var pid = pageProductId();
    if (!pid) return;
    var target = answerKey(pid);
    if (!target) return;
    var base = catalogBase();
    if (!base) return;

    var kP = target.p / base;               /* base-price scale (1.0 when no test / this side unchanged) */
    var cap = containerCap(container, base); /* catalog compare-at, if the product has one */
    var kC = cap && target.cap ? target.cap / cap : null;

    var key = widgetState(container);
    var bars = container.querySelectorAll('.rapi-bundles__bar');
    var i;
    for (i = 0; i < bars.length; i++) {
      try {
        var bar = bars[i];
        var els = barEls(bar);
        if (!els.price) continue;
        var b = baseline(bar, key, els);
        if (!b) continue;

        writeCents(els.price, Math.round(b.price * kP), b.price);

        /* classify the struck value: cap-derived (compare_at x qty, both
           toggle states on compare-at products) or base-derived (one-time
           price echo / base multiple on no-cap products) */
        var fullIsCap = !!(b.full && cap && nearMultiple(b.full, cap, Math.max(2, Math.round(b.full / cap))));

        var fullVisible = false;
        if (els.full && b.full) {
          if (fullIsCap) {
            if (kC) {
              setHidden(els.full, false);
              writeCents(els.full, Math.round(b.full * kC), b.full);
              fullVisible = true;
            } else {
              /* test experience removed the compare-at: hide the strike + savings */
              setHidden(els.full, true);
            }
          } else {
            setHidden(els.full, false);
            writeCents(els.full, Math.round(b.full * kP), b.full);
            fullVisible = true;
          }
        }

        if (els.save) {
          if (fullVisible && b.save && b.full && b.full > b.price) {
            /* quantity factor learned from Rapi's own baseline (pack size on
               per-unit displays, 1 on totals), reapplied to what we wrote so
               the subtitle always agrees with the visible prices */
            var q = b.save / (b.full - b.price);
            var fullNow = toCents(els.full.textContent);
            var priceNow = toCents(els.price.textContent);
            if (isFinite(q) && q > 0 && fullNow && priceNow && fullNow > priceNow) {
              setHidden(els.save, false);
              writeCents(els.save, Math.round(q * (fullNow - priceNow)), b.save);
            }
          } else if (els.full && fullIsCap && !kC) {
            setHidden(els.save, true);
          }
        }
      } catch (e) { log('bar skipped', e); }
    }
  }

  /* ---------- wiring ---------- */

  var observers = [];
  var runQueued = false;

  function runNow() {
    runQueued = false;
    if (window.__slRapiDisplayOff) return;
    var containers = document.querySelectorAll('.rapi-bundles');
    var i;
    for (i = 0; i < containers.length; i++) {
      try { correct(containers[i]); } catch (e) { log('container skipped', e); }
    }
    /* Flush the mutation records our own writes just produced so they do not
       re-trigger a pass. If any flushed record's element is NOT showing our
       text, a third-party write interleaved with this pass - re-schedule so
       it gets corrected instead of silently discarded. */
    var again = false;
    for (i = 0; i < observers.length; i++) {
      var recs;
      try { recs = observers[i].takeRecords(); } catch (e) { recs = []; }
      if (again) continue;
      for (var j = 0; j < recs.length; j++) {
        var t = recs[j].target;
        var el = t && t.nodeType === 3 ? t.parentElement : t;
        if (el && el.nodeType === 1 && !isOurText(el)) { again = true; break; }
      }
    }
    if (again) schedule();
  }
  function schedule() {
    if (runQueued) return;
    runQueued = true;
    setTimeout(runNow, 60);
  }

  function init() {
    var containers = document.querySelectorAll('.rapi-bundles');
    var i;
    for (i = 0; i < containers.length; i++) {
      var mo = new MutationObserver(schedule);
      mo.observe(containers[i], { childList: true, characterData: true, subtree: true });
      observers.push(mo);
    }
    /* re-run when Shoplift (re-)applies the theme swap, e.g. after
       variant-picker rewrites or late runtime application */
    var pid = pageProductId();
    var key = pid && answerKey(pid);
    if (key && key.pEl) {
      var mo2 = new MutationObserver(schedule);
      mo2.observe(key.pEl, { childList: true, characterData: true, subtree: true });
      observers.push(mo2);
    }
    /* toggling the subscribe checkbox re-renders bar text (observed), but the
       change event is the reliable early signal for state-key switches */
    document.addEventListener('change', function (e) {
      try { if (e.target && e.target.name === 'custom-box') schedule(); } catch (err) {}
    }, true);

    status.active = true;
    status.why = 'active: ' + containers.length + ' container(s)';
    log(status.why);
    runNow();
  }

  /* Wait for both the Shoplift runtime and a rendered Rapi widget (they load
     independently). Anything missing after 15s: stay dormant, silently. */
  var tries = 0;
  var poll = setInterval(function () {
    tries += 1;
    try {
      if (window.__slRapiDisplayOff) { clearInterval(poll); status.why = 'kill switch'; return; }
      var widget = document.querySelector('.rapi-bundles .rapi-bundles__bar');
      if (window.shopliftInstance && widget && pageProductId()) {
        clearInterval(poll);
        try { init(); } catch (e) { status.why = 'init failed; dormant'; log(status.why, e); }
        return;
      }
      /* fast exit: page fully loaded with no product form and no widget
         (collections, cart, home) - nothing for this layer to do */
      if (document.readyState === 'complete' && !widget && !pageProductId() && tries > 8) {
        clearInterval(poll);
        status.why = 'no product form / widget; dormant';
        log(status.why);
        return;
      }
    } catch (e) {}
    if (tries > 60) { clearInterval(poll); status.why = 'timed out waiting for runtime/widget; dormant'; log(status.why); }
  }, 250);
})();
